import { create } from 'zustand';
import type { ChatRoom, ChatMessage, AiConversation, AiMessage } from '@vtak/shared';
import { chatClient } from '@/services/chat-client';

interface ChatState {
  rooms: ChatRoom[];
  /** Chat tab state */
  messages: ChatMessage[];
  activeRoomId: string;
  /** AI conversation state */
  aiConversations: AiConversation[];
  activeConversationId: string;
  aiMessages: AiMessage[];
  isAiStreaming: boolean;
  aiStreamContent: string;
  aiError: string | null;
  serviceAvailable: boolean;

  setRooms: (rooms: ChatRoom[]) => void;
  setActiveRoomId: (id: string) => void;
  setMessages: (messages: ChatMessage[]) => void;
  addMessage: (message: ChatMessage) => void;

  /** AI conversation actions */
  setActiveConversationId: (id: string) => void;
  startAiStream: () => void;
  appendAiChunk: (chunk: string) => void;
  finishAiStream: (assistantMessage: AiMessage) => void;
  failAiStream: (error: string) => void;
  clearAiError: () => void;
  addUserMessage: (message: AiMessage) => void;

  loadRooms: () => Promise<void>;
  loadMessages: (roomId: string) => Promise<void>;
  loadAiConversations: () => Promise<void>;
  loadAiMessages: (conversationId: string) => Promise<void>;
  createAiConversation: () => Promise<string>;
  deleteAiConversation: (id: string) => Promise<void>;
}

/** Store for chat rooms, messages, and AI conversation state */
export const useChatStore = create<ChatState>((set, get) => ({
  rooms: [],
  messages: [],
  activeRoomId: '',
  aiConversations: [],
  activeConversationId: '',
  aiMessages: [],
  isAiStreaming: false,
  aiStreamContent: '',
  aiError: null,
  serviceAvailable: true,

  setRooms: (rooms) => set({ rooms }),
  setActiveRoomId: (id) => set({ activeRoomId: id }),
  setMessages: (messages) => set({ messages }),

  addMessage: (message) =>
    set((state) => {
      if (message.roomId === state.activeRoomId) {
        return { messages: [...state.messages, message] };
      }
      return state;
    }),

  setActiveConversationId: (id) => set({ activeConversationId: id }),

  addUserMessage: (message) =>
    set((state) => ({ aiMessages: [...state.aiMessages, message] })),

  startAiStream: () =>
    set({ isAiStreaming: true, aiStreamContent: '', aiError: null }),

  appendAiChunk: (chunk) =>
    set((state) => ({ aiStreamContent: state.aiStreamContent + chunk })),

  finishAiStream: (assistantMessage) =>
    set((state) => ({
      isAiStreaming: false,
      aiStreamContent: '',
      aiMessages: [...state.aiMessages, assistantMessage],
    })),

  failAiStream: (error) =>
    set({ isAiStreaming: false, aiStreamContent: '', aiError: error }),

  clearAiError: () => set({ aiError: null }),

  loadRooms: async () => {
    try {
      const rooms = await chatClient.getRooms();
      set({ rooms, serviceAvailable: true });
    } catch {
      set({ serviceAvailable: false });
    }
  },

  loadMessages: async (roomId) => {
    try {
      const messages = await chatClient.getMessages(roomId);
      set({ messages });
    } catch {
      /* server unavailable */
    }
  },

  loadAiConversations: async () => {
    try {
      const conversations = await chatClient.getAiConversations();
      set({ aiConversations: conversations, serviceAvailable: true });
    } catch {
      set({ serviceAvailable: false });
    }
  },

  loadAiMessages: async (conversationId) => {
    try {
      const messages = await chatClient.getAiMessages(conversationId);
      set({ aiMessages: messages, activeConversationId: conversationId });
    } catch {
      /* server unavailable */
    }
  },

  createAiConversation: async () => {
    const conversation = await chatClient.createAiConversation();
    const conversations = get().aiConversations;
    set({
      aiConversations: [conversation, ...conversations],
      activeConversationId: conversation.id,
      aiMessages: [],
    });
    return conversation.id;
  },

  deleteAiConversation: async (id) => {
    await chatClient.deleteAiConversation(id);
    const state = get();
    const remaining = state.aiConversations.filter((c) => c.id !== id);
    const updates: Partial<ChatState> = { aiConversations: remaining };
    if (state.activeConversationId === id) {
      updates.activeConversationId = '';
      updates.aiMessages = [];
    }
    set(updates);
  },
}));
